import { IProductInfo } from "../../home/types/Menu";

type CartLine = { id: number; quantity: number };
type LimitedCartLine = CartLine & Pick<IProductInfo, "max_cart_qty">;

/**
 * Лимит товара в корзине (`max_cart_qty` с бэкенда). null — без ограничений.
 * Ноль и мусор тоже считаем «без ограничений»: запретить товар целиком —
 * не задача этого поля, его просто не было бы в меню.
 */
export const getMaxCartQty = (
  product?: Pick<IProductInfo, "max_cart_qty"> | null,
): number | null => {
  const limit = product?.max_cart_qty;
  return typeof limit === "number" && limit > 0 ? limit : null;
};

/**
 * Сколько штук товара уже в корзине. Лимит — на товар, а не на строку:
 * один и тот же товар с разными модификаторами лежит в разных строках
 * (разные cartKey), и считаются они вместе.
 */
export const getProductCartQuantity = (items: CartLine[], productId: number) =>
  items.reduce(
    (sum, item) => (item.id === productId ? sum + item.quantity : sum),
    0,
  );

/** Ещё одну штуку товара добавить уже нельзя. */
export const isCartLimitReached = (
  items: CartLine[],
  product: Pick<IProductInfo, "id" | "max_cart_qty">,
) => {
  const limit = getMaxCartQty(product);
  return limit !== null && getProductCartQuantity(items, product.id) >= limit;
};

/**
 * Срезать количества до лимита. Корзина могла собраться до того, как у товара
 * появился max_cart_qty (или лимит уменьшили), — тогда на чекаут ушло бы
 * больше разрешённого. Строки одного товара заполняются по порядку, лишние
 * убираются целиком.
 */
export const clampToCartLimits = <T extends LimitedCartLine>(items: T[]): T[] => {
  const left = new Map<number, number>();

  return items.flatMap((item) => {
    const limit = getMaxCartQty(item);
    if (limit === null) return [item];

    const available = left.get(item.id) ?? limit;
    const quantity = Math.min(item.quantity, available);
    left.set(item.id, available - quantity);

    if (quantity <= 0) return [];
    return [quantity === item.quantity ? item : { ...item, quantity }];
  });
};
